package com.merkost.handpass.admin.auth

import androidx.compose.runtime.*
import com.merkost.handpass.admin.components.AdminButton
import com.merkost.handpass.admin.components.AdminButtonVariant
import com.merkost.handpass.admin.components.AdminInput
import com.merkost.handpass.sharedweb.theme.Bg
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusCard
import com.merkost.handpass.sharedweb.theme.Shadow
import com.merkost.handpass.sharedweb.theme.Surface
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun LoginPage(onLogin: (email: String, password: String) -> Unit, onGoogleSignIn: () -> Unit) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            property("background-color", Bg.value().toString())
            property("font-family", HandpassTypography.FontFamily)
            property("padding", "${HandpassSpacing.Default}")
        }
    }) {
        Div({
            style {
                width(400.px)
                property("max-width", "100%")
                property("box-sizing", "border-box")
                padding(HandpassSpacing.XXXL)
                property("background-color", Surface.value().toString())
                borderRadius(RadiusCard.value())
                property("box-shadow", "var(--hp-shadow-card)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    property("text-align", "center")
                    property("margin-bottom", "${HandpassSpacing.XL}")
                }
            }) {
                Img(src = "/logo.svg", attrs = {
                    attr("alt", "Handpass")
                    attr("width", "48")
                    attr("height", "48")
                    style {
                        width(48.px)
                        height(48.px)
                        property("margin-bottom", "${HandpassSpacing.MD}")
                    }
                })
                H1({
                    style {
                        fontSize(HandpassTypography.XL)
                        fontWeight(700)
                        color(OnSurface.value())
                        property("margin", "0")
                        property("letter-spacing", "-0.01em")
                    }
                }) { Text("Handpass Admin") }
                P({
                    style {
                        fontSize(HandpassTypography.Body)
                        color(OnSurfaceMuted.value())
                        property("margin", "${HandpassSpacing.XS} 0 0 0")
                    }
                }) { Text("Sign in to the moderation console") }
            }

            Form(attrs = {
                onSubmit {
                    it.preventDefault()
                    onLogin(email, password)
                }
            }) {
                Div({ style { property("margin-bottom", HandpassSpacing.MD.toString()) } }) {
                    AdminInput(
                        value = email,
                        onChange = { email = it },
                        placeholder = "Email",
                        type = InputType.Email,
                        ariaLabel = "Email",
                    )
                }

                Div({ style { property("margin-bottom", HandpassSpacing.XL.toString()) } }) {
                    AdminInput(
                        value = password,
                        onChange = { password = it },
                        placeholder = "Password",
                        type = InputType.Password,
                        ariaLabel = "Password",
                    )
                }

                AdminButton(
                    label = "Sign In",
                    onClick = {},
                    variant = AdminButtonVariant.Primary,
                    fillWidth = true,
                )
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(HandpassSpacing.SM)
                    property("margin", "${HandpassSpacing.XL} 0 ${HandpassSpacing.Default} 0")
                }
            }) {
                Div({ style { property("flex", "1"); height(1.px); property("background-color", Border.value().toString()) } })
                Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()) } }) { Text("or") }
                Div({ style { property("flex", "1"); height(1.px); property("background-color", Border.value().toString()) } })
            }

            AdminButton(
                label = "Sign in with Google",
                onClick = { onGoogleSignIn() },
                variant = AdminButtonVariant.Secondary,
                fillWidth = true,
                leadingContent = { GoogleGIcon() },
            )
        }
    }
}

@Composable
private fun GoogleGIcon() {
    Div(attrs = {
        style {
            display(DisplayStyle.LegacyInlineFlex)
            width(18.px)
            height(18.px)
            property("flex-shrink", "0")
        }
        ref { el ->
            el.innerHTML = GOOGLE_G_SVG
            onDispose { }
        }
    })
}

private const val GOOGLE_G_SVG =
    "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"18\" height=\"18\" viewBox=\"0 0 18 18\">" +
        "<path fill=\"#4285F4\" d=\"M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z\"/>" +
        "<path fill=\"#34A853\" d=\"M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z\"/>" +
        "<path fill=\"#FBBC05\" d=\"M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z\"/>" +
        "<path fill=\"#EA4335\" d=\"M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z\"/>" +
        "</svg>"

@Composable
fun LoadingScreen() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            property("background-color", Bg.value().toString())
            property("font-family", HandpassTypography.FontFamily)
        }
    }) {
        P({ style { fontSize(HandpassTypography.MD); color(OnSurfaceMuted.value()) } }) { Text("Loading...") }
    }
}

@Composable
fun AccessDeniedPage(email: String, onLogout: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            property("background-color", Bg.value().toString())
            property("font-family", HandpassTypography.FontFamily)
        }
    }) {
        Div({ style { property("text-align", "center"); padding(HandpassSpacing.XXXL) } }) {
            H2({
                style { fontSize(HandpassTypography.Title); color(OnSurface.value()); property("margin-bottom", HandpassSpacing.MD.toString()) }
            }) { Text("Access Denied") }
            P({
                style { fontSize(HandpassTypography.Body); color(OnSurfaceMuted.value()); property("margin-bottom", HandpassSpacing.XL.toString()) }
            }) { Text("$email is not an administrator account.") }
            AdminButton(
                label = "Sign out",
                onClick = { onLogout() },
                variant = AdminButtonVariant.Secondary,
            )
        }
    }
}

@Composable
fun ErrorScreen(message: String, onRetry: () -> Unit, onLogout: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            property("background-color", Bg.value().toString())
            property("font-family", HandpassTypography.FontFamily)
        }
    }) {
        Div({ style { property("text-align", "center"); padding(HandpassSpacing.XXXL); property("max-width", "420px") } }) {
            H2({
                style { fontSize(HandpassTypography.Title); color(OnSurface.value()); property("margin-bottom", HandpassSpacing.MD.toString()) }
            }) { Text("Something went wrong") }
            P({
                style { fontSize(HandpassTypography.Body); color(OnSurfaceMuted.value()); property("margin-bottom", HandpassSpacing.XL.toString()) }
            }) { Text(message) }
            Div({ style { property("margin-bottom", HandpassSpacing.MD.toString()) } }) {
                AdminButton(
                    label = "Retry",
                    onClick = { onRetry() },
                    variant = AdminButtonVariant.Primary,
                )
            }
            AdminButton(
                label = "Sign out",
                onClick = { onLogout() },
                variant = AdminButtonVariant.Secondary,
            )
        }
    }
}
